/**
 * InspectorTabStrip — the Node Inspector's primary navigation.
 *
 * The strip this replaced was a single `overflow-x-auto hide-scrollbar` row that
 * needed ~820px: seven `text-sm px-3` labels (~560px), seven inline ⓘ buttons
 * (~130px), and the ORIG/USD toggle plus its own ⓘ (~130px). The panel caps at
 * 780px and loses 32px to padding, so the row overflowed even fully dragged out
 * — Actions and Forensic were unreachable at every width, with `hide-scrollbar`
 * removing the only clue that more tabs existed.
 *
 * Three changes make all seven fit at any width, with nothing to scroll:
 *
 *   1. The row WRAPS (`flex-wrap`). Two rows at 440px, one when dragged wide.
 *      Wrapping is why the tabs are pills rather than underlines — a bottom
 *      border on a wrapped row reads as a divider through the middle of the nav.
 *   2. Seven inline ⓘ buttons collapse into one hint line under the strip that
 *      describes the active tab. Cheaper horizontally and better pedagogy: the
 *      explanation is simply present instead of needing a hover to find.
 *   3. ORIG/USD moved out to the panel utility row (it is a panel-wide display
 *      preference, not a tab), returning ~130px to the labels.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Info, X } from 'lucide-react';
import { clsx } from 'clsx';
import { INSPECTOR_TABS, isTabActive } from './inspectorTabs';
import type { InspectorTab } from './types';

function tabClass(active: boolean): string {
    return clsx(
        'inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-[13px] font-medium whitespace-nowrap transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40',
        active
            ? 'bg-primary text-primary-foreground shadow-sm'
            : 'bg-secondary/50 text-muted-foreground hover:bg-secondary hover:text-foreground',
    );
}

interface Props {
    activeTab: InspectorTab;
    onSelect: (tab: InspectorTab) => void;
    /** Transfer count badge — number of transactions loaded for this node. */
    txCount?: number;
    /** Intel tab spinner while the dossier fans out. */
    intelLoading?: boolean;
    /** Forensic tab pulse when subpoena records exist for this node. */
    forensicHasData?: boolean;
}

export const InspectorTabStrip: React.FC<Props> = ({
    activeTab,
    onSelect,
    txCount = 0,
    intelLoading = false,
    forensicHasData = false,
}) => {
    const activeDef = INSPECTOR_TABS.find(t => isTabActive(t.id, activeTab)) ?? INSPECTOR_TABS[0];

    // Standard tablist keys: arrows move selection, Home/End jump to the ends.
    // Siblings are read off the DOM so roving focus needs no ref bookkeeping.
    const handleKeyDown = useCallback((event: React.KeyboardEvent<HTMLButtonElement>) => {
        const step = event.key === 'ArrowRight' ? 1 : event.key === 'ArrowLeft' ? -1 : 0;
        const isEdgeKey = event.key === 'Home' || event.key === 'End';
        if (step === 0 && !isEdgeKey) return;

        const tabs = Array.from(
            event.currentTarget.parentElement?.querySelectorAll<HTMLButtonElement>('[role="tab"]') ?? [],
        );
        if (tabs.length === 0) return;

        const current = tabs.indexOf(event.currentTarget);
        const next = isEdgeKey
            ? (event.key === 'Home' ? 0 : tabs.length - 1)
            : (current + step + tabs.length) % tabs.length;

        event.preventDefault();
        const target = tabs[next];
        target.focus();
        const id = target.dataset.tab as InspectorTab | undefined;
        if (id) onSelect(id);
    }, [onSelect]);

    return (
        <div className="shrink-0 mb-3" data-testid="wb-inspector-tabs">
            <div
                role="tablist"
                aria-label="Node Inspector sections"
                className="flex flex-wrap gap-1 pb-2 border-b border-border/50"
            >
                {INSPECTOR_TABS.map(tab => {
                    const active = isTabActive(tab.id, activeTab);
                    return (
                        <button
                            key={tab.id}
                            type="button"
                            role="tab"
                            data-tab={tab.id}
                            aria-selected={active}
                            tabIndex={active ? 0 : -1}
                            title={`${tab.label} — ${tab.hint}`}
                            onClick={() => onSelect(tab.id)}
                            onKeyDown={handleKeyDown}
                            className={tabClass(active)}
                            data-tour={tab.id === 'analysis' ? 'analysis-tab' : undefined}
                        >
                            <tab.icon className="w-3.5 h-3.5 shrink-0" aria-hidden />
                            {tab.label}
                            {tab.id === 'transfers' && txCount > 0 && (
                                <span className={clsx(
                                    'px-1.5 rounded-full text-[11px] font-semibold',
                                    active ? 'bg-primary-foreground/20' : 'bg-primary/20 text-primary',
                                )}>
                                    {txCount}
                                </span>
                            )}
                            {tab.id === 'intelligence' && intelLoading && (
                                <span className={clsx(
                                    'inline-block w-2.5 h-2.5 rounded-full border-2 border-t-transparent animate-spin',
                                    active ? 'border-primary-foreground' : 'border-primary',
                                )} />
                            )}
                            {tab.id === 'forensic' && forensicHasData && (
                                <span className={clsx(
                                    'w-2 h-2 rounded-full animate-pulse',
                                    active ? 'bg-primary-foreground' : 'bg-primary',
                                )} />
                            )}
                        </button>
                    );
                })}
            </div>
            <ActiveTabHint hint={activeDef.hint} />
        </div>
    );
};

const HINT_DISMISS_KEY = 'explainer-dismissed-inspector-tabs';

/**
 * One-line description of the active tab. Follows the same storage contract as
 * FeatureExplainer so Settings → Explain Mode turns it back on.
 */
const ActiveTabHint: React.FC<{ hint: string }> = ({ hint }) => {
    const [hidden, setHidden] = useState(() => {
        if (typeof localStorage === 'undefined') return false;
        if (localStorage.getItem('explainerMode') === 'off') return true;
        return localStorage.getItem(HINT_DISMISS_KEY) === 'true';
    });

    useEffect(() => {
        const handler = (event: StorageEvent) => {
            if (event.key !== 'explainerMode') return;
            if (event.newValue === 'detailed') {
                localStorage.removeItem(HINT_DISMISS_KEY);
                setHidden(false);
            } else if (event.newValue === 'off') {
                setHidden(true);
            }
        };
        window.addEventListener('storage', handler);
        return () => window.removeEventListener('storage', handler);
    }, []);

    if (hidden) return null;

    return (
        <div className="flex items-start gap-1.5 pt-1.5 px-0.5" data-testid="wb-inspector-tab-hint">
            <Info className="w-3 h-3 mt-[2px] shrink-0 text-muted-foreground/60" aria-hidden />
            <p className="flex-1 text-[11px] leading-snug text-muted-foreground/80">{hint}</p>
            <button
                type="button"
                onClick={() => {
                    localStorage.setItem(HINT_DISMISS_KEY, 'true');
                    setHidden(true);
                }}
                aria-label="Hide tab explanations"
                title="Hide these one-line tab explanations (Settings → Explain Mode restores them)"
                className="shrink-0 p-0.5 -mt-0.5 rounded text-muted-foreground/50 hover:text-foreground hover:bg-muted transition-colors"
            >
                <X className="w-3 h-3" />
            </button>
        </div>
    );
};
